import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Landmark } from 'lucide-react';
import type { RemittanceDto } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, newIdempotencyKey } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { minorFromInput } from '@/components/MoneyInput';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { insuranceTabPath } from './insurance-routes';

const toMinor = (value: string) => minorFromInput(value);

export function RecordRemittancePage() {
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), insuranceTabPath('payments'));
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initial = { payerId: '', reference: '', paymentDate: new Date().toISOString().slice(0, 10), method: 'bank_transfer', bankRef: '', total: '', fee: '', withholding: '' };
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [key] = useState(() => newIdempotencyKey());
  const payers = useQuery({ queryKey: ['payers'], queryFn: () => get<{ id: string; name: string }[]>('/insurance/payers') });
  const save = useMutation({
    mutationFn: () =>
      post<RemittanceDto>(
        '/insurance/payment-reconciliations',
        {
          payerId: form.payerId,
          reference: form.reference.trim(),
          paymentDate: form.paymentDate,
          method: form.method,
          ...(form.bankRef.trim() ? { bankRef: form.bankRef.trim() } : {}),
          totalMinor: toMinor(form.total),
          feeMinor: toMinor(form.fee),
          withholdingTaxMinor: toMinor(form.withholding),
        },
        { idempotencyKey: key },
      ),
    onSuccess: (created) => {
      toast.success('Payment recorded', 'Allocate it to the claims it settles');
      void queryClient.invalidateQueries({ queryKey: ['remittances'] });
      navigate(`${returnTo}${returnTo.includes('?') ? '&' : '?'}remittance=${created.id}`, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });
  const ready = Boolean(form.payerId) && form.reference.trim().length > 0 && toMinor(form.total) > 0;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title="Record a payment received"
      description="Recording it posts nothing; the money reaches the claims when you allocate it"
      icon={Landmark}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <FormSection title={tr('billing', 'payment')} icon={Landmark}>
        <Field label="Payer" required>
          <Select value={form.payerId} onChange={(e) => setForm({ ...form, payerId: e.target.value })}>
            <option value="">Choose…</option>
            {payers.data?.map((payer) => (
              <option key={payer.id} value={payer.id}>
                {payer.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Payer’s reference" required>
          <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
        </Field>
        <Field label="Paid on" required>
          <DatePicker value={form.paymentDate} onChange={(value) => setForm({ ...form, paymentDate: value })} />
        </Field>
        <Field label="How">
          <ChoiceButtons
            layout="chips"
            value={form.method}
            onChange={(method) => setForm({ ...form, method })}
            options={['bank_transfer', 'cheque', 'cash', 'card', 'other'].map((value) => ({ value, label: humanize(value, 'payment_mode') }))}
          />
        </Field>
        <Field label="Bank or cheque reference" className="sm:col-span-2">
          <Input value={form.bankRef} onChange={(e) => setForm({ ...form, bankRef: e.target.value })} />
        </Field>
      </FormSection>
      <FormSection title={tr('billing', 'amounts')} icon={Banknote} columns={3}>
        <Field label="Amount the payer settled" required>
          <Input type="text" dir="ltr" inputMode="decimal" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} />
        </Field>
        <Field label="Fees the payer took">
          <Input type="text" dir="ltr" inputMode="decimal" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} />
        </Field>
        <Field label="Tax withheld">
          <Input type="text" dir="ltr" inputMode="decimal" value={form.withholding} onChange={(e) => setForm({ ...form, withholding: e.target.value })} />
        </Field>
      </FormSection>
      {error && <Alert variant="destructive">{error}</Alert>}
      <FormActions submitLabel="Record" loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
